/**
 * Geocoding Service using Mapbox API
 *
 * Handles address geocoding with intelligent caching to minimize API costs.
 * All geocoded addresses are cached in the database (clients.latitude/longitude).
 */

interface GeocodeResult {
  latitude: number
  longitude: number
  formattedAddress: string
}

interface MapboxFeature {
  center: [number, number] // [longitude, latitude]
  place_name: string
  relevance: number
}

interface MapboxResponse {
  features: MapboxFeature[]
}

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

if (!MAPBOX_ACCESS_TOKEN) {
  console.warn('⚠️ VITE_MAPBOX_PUBLIC_TOKEN not found in environment variables')
}

/**
 * Geocode an address using Mapbox Geocoding API
 * Returns lat/long coordinates and formatted address
 */
export async function geocodeAddress(
  address: string,
  city?: string,
  province?: string,
  postalCode?: string
): Promise<GeocodeResult | null> {
  if (!MAPBOX_ACCESS_TOKEN) {
    console.error('Mapbox API key not configured')
    return null
  }

  // Build full address string
  const addressParts = [address, city, province, postalCode].filter(Boolean)
  const fullAddress = addressParts.join(', ')

  if (!fullAddress.trim()) {
    console.error('Cannot geocode empty address')
    return null
  }

  try {
    // Encode address for URL
    const encodedAddress = encodeURIComponent(fullAddress)

    // Call Mapbox Geocoding API
    // Bias results to North America (USA + Canada)
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=us,ca&limit=1`

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`)
    }

    const data: MapboxResponse = await response.json()

    if (!data.features || data.features.length === 0) {
      console.warn('No geocoding results found for:', fullAddress)
      return null
    }

    const feature = data.features[0]
    const [longitude, latitude] = feature.center

    return {
      latitude,
      longitude,
      formattedAddress: feature.place_name
    }
  } catch (error) {
    console.error('Geocoding error:', error)
    return null
  }
}

/**
 * Geocode coordinates (lat/long) to an address (reverse geocoding)
 * Used for pin placement on map
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<GeocodeResult | null> {
  if (!MAPBOX_ACCESS_TOKEN) {
    console.error('Mapbox API key not configured')
    return null
  }

  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=us,ca&limit=1`

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`)
    }

    const data: MapboxResponse = await response.json()

    if (!data.features || data.features.length === 0) {
      console.warn('No reverse geocoding results found for:', latitude, longitude)
      return null
    }

    const feature = data.features[0]

    return {
      latitude,
      longitude,
      formattedAddress: feature.place_name
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error)
    return null
  }
}

/**
 * Batch geocode multiple addresses
 * Returns results in same order as input, with null for failed geocodes
 */
export async function batchGeocodeAddresses(
  addresses: Array<{
    address?: string
    city?: string
    province?: string
    postalCode?: string
  }>
): Promise<Array<GeocodeResult | null>> {
  // Mapbox doesn't have native batch geocoding, so we'll rate limit ourselves
  // to avoid hitting API limits (600 requests/minute for free tier)
  const DELAY_MS = 100 // 10 requests/second = 600/minute

  const results: Array<GeocodeResult | null> = []

  for (const addr of addresses) {
    const result = await geocodeAddress(
      addr.address || '',
      addr.city,
      addr.province,
      addr.postalCode
    )
    results.push(result)

    // Delay between requests
    if (addresses.indexOf(addr) < addresses.length - 1) {
      await new Promise(resolve => setTimeout(resolve, DELAY_MS))
    }
  }

  return results
}
